import { useState } from "react";
import { omit } from "lodash";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import type { QuizQuestion } from "@/react/types/QuizQuestion";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { MODAL_SIZES } from "@circle-react-uikit/ModalV2/ModalContent";
import { Typography } from "@circle-react-uikit/Typography";
import type { OptionPayload } from "./QuestionForm";
import { QuestionForm } from "./QuestionForm";

export interface QuestionPayload
  extends Omit<QuizQuestion, "id" | "statement" | "options"> {
  _destroy?: boolean;
  id?: number;
  options: OptionPayload[];
  statement?: string;
}

interface QuestionModalComponentProps {
  isEdit: boolean;
  onSubmit: (index: number, question: QuestionPayload) => void;
  question: QuestionPayload;
  questionIndex: number;
}
const QuestionModalComponent = ({
  question,
  questionIndex,
  onSubmit,
  isEdit,
}: QuestionModalComponentProps) => {
  const [hasCorrectAnswersError, setHasCorrectAnswersError] = useState(false);
  const [isCoverMediaEnabled, setIsCoverMediaEnabled] = useState<boolean>(
    !!question.cover_media?.signed_id,
  );
  const modal = useModal();
  if (!modal || !modal.visible) return null;

  const checkCorrectAnswersError = (
    payload: Omit<QuestionPayload, "cover_media">,
  ) => {
    const options = payload.options;
    const isSingleChoice = payload.question_type === "single_choice";
    const isMultipleChoice = payload.question_type === "multiple_choice";
    const correctAnswers = options.filter(
      (option: OptionPayload) => option.correct,
    );
    const hasError =
      (isSingleChoice && correctAnswers.length !== 1) ||
      (isMultipleChoice && correctAnswers.length == 0);
    setHasCorrectAnswersError(hasError);
    return hasError;
  };

  const actionLabel = t(
    `courses.edit.lesson.${isEdit ? "edit" : "add"}_question`,
  );
  const buttonLabel = t(
    `courses.edit.lesson.${isEdit ? "update" : "add"}_question`,
  );

  return (
    <Modal isOpen={modal.visible} contentPortalId="question-modal-portal">
      <Modal.Overlay onClick={modal.hide} />
      <Modal.Content size={MODAL_SIZES.lg}>
        <Modal.Header className="z-10 md:!py-4">
          <Typography.TitleMd>{actionLabel}</Typography.TitleMd>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="question-form"
            onSubmit={(payload: QuestionPayload) => {
              if (checkCorrectAnswersError(payload)) {
                return;
              }
              const hasCoverMedia =
                isCoverMediaEnabled && !!payload.cover_media?.signed_id;
              const filteredPayload = {
                ...omit(payload, "cover_media_enabled", "cover_media"),
                ...{
                  cover_media: (hasCoverMedia && payload.cover_media) || null,
                },
              };
              onSubmit(questionIndex, filteredPayload);
              void modal.hide();
            }}
            defaultValues={question}
          >
            <QuestionForm
              checkCorrectAnswersError={checkCorrectAnswersError}
              hasCorrectAnswersError={hasCorrectAnswersError}
              setIsCoverMediaEnabled={setIsCoverMediaEnabled}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-t-primary border-t pb-6 pt-4">
          <div className="flex justify-end gap-4">
            <Button
              large
              type="button"
              variant="secondary"
              onClick={modal.hide}
            >
              {t("cancel")}
            </Button>
            <Button
              large
              type="submit"
              variant="circle"
              form="question-form"
              disabled={hasCorrectAnswersError}
            >
              {buttonLabel}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
const QuestionModal = ModalManager.create(
  (props: QuestionModalComponentProps) => <QuestionModalComponent {...props} />,
);
export const useQuestionModal = () => useModal(QuestionModal);
